// Reading a widget message out of the transcript.
//
// A widget message IS Mira's question, but its `text` is EMPTY: the question
// lives in `metadata.widget.question` and the tappable choices in
// `metadata.widget.options` (backend `agent/conversation.py` says the same thing
// for the agents' own history render). The admin thread used to fall back to a
// literal "Interactive prompt" for those rows, so an operator reading a
// conversation saw a placeholder exactly where Mira had asked the question that
// shaped everything after it.
//
// Once the client answers, the backend stamps the response onto the SAME message
// under `metadata.widgetResponse` (repo/workspace.py::save_widget_response), so
// one row carries both halves and the reader can show what was asked AND what
// was picked.
//
// Everything here is defensive: `metadata` is an untyped bag on the wire shape
// (`Record<string, unknown>`), and this is an audit view of historical rows
// written by many versions of the agent. A malformed widget degrades to "no
// question" rather than throwing inside the transcript render.
import type { SupportChatMessage } from "@recruiter/shared/types";

export interface WidgetSummary {
  /** Mira's question. Empty only for a malformed widget. */
  question: string;
  /** The tappable choices she offered, in order. */
  options: string[];
  /** What the client picked, already formatted. Null while unanswered. */
  answer: string | null;
  /** True when the client dismissed the card instead of answering. */
  skipped: boolean;
}

function bag(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function str(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

/** Option labels, in the order Mira offered them. An option with no usable
 *  label is dropped rather than rendered as a blank chip. */
function optionLabels(widget: Record<string, unknown>): string[] {
  const raw = widget["options"];
  if (!Array.isArray(raw)) return [];
  return raw.map((option) => str(bag(option)?.["label"])).filter((label) => label !== "");
}

/**
 * Format a widget answer for a human.
 *
 * The value is a union across widget kinds (`WidgetValue`): a picked option id
 * or free text, a multi-select array, a number, or a budget range object. Each
 * gets a readable rendering; anything unrecognised falls back to JSON so an
 * operator sees the raw answer rather than nothing.
 */
export function formatWidgetValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number") return String(value);
  if (typeof value === "boolean") return value ? "yes" : "no";
  if (Array.isArray(value)) {
    const parts = value.map((entry) => formatWidgetValue(entry)).filter((p) => p !== "");
    return parts.join(", ");
  }
  const range = bag(value);
  if (range) {
    // A budget answer: {min?, max?, period?, currency?}. Rendered as the range
    // the client actually set, not as every key it happens to carry.
    const currency = str(range["currency"]);
    const min = range["min"];
    const max = range["max"];
    const amount = (n: unknown): string =>
      typeof n === "number" ? `${currency ? `${currency} ` : ""}${n.toLocaleString()}` : "";
    const lo = amount(min);
    const hi = amount(max);
    const span = lo && hi ? (lo === hi ? lo : `${lo} - ${hi}`) : lo || hi;
    const period = str(range["period"]);
    if (span) return period ? `${span} / ${period}` : span;
    return JSON.stringify(value);
  }
  return JSON.stringify(value);
}

/**
 * The widget carried by this message, or null when there is none to render.
 *
 * Null means "fall back to the message's own text" — for a non-widget message,
 * and for a widget row so malformed that it has no question, which is a broken
 * row rather than an interactive one.
 */
export function widgetSummary(message: SupportChatMessage): WidgetSummary | null {
  if (message.type !== "widget") return null;
  const widget = bag(bag(message.metadata)?.["widget"]);
  if (!widget) return null;

  const question = str(widget["question"]);
  if (!question) return null;

  const response = bag(bag(message.metadata)?.["widgetResponse"]);
  const skipped = Boolean(response?.["skipped"]);
  const answer = response && !skipped ? formatWidgetValue(response["value"]) : "";

  return {
    question,
    options: optionLabels(widget),
    answer: answer || null,
    skipped,
  };
}
